import { Navigate, type RouteObject } from "react-router";
import { InstructionsTab } from "../../features/agents/static/instructionsTab.js";
import { ResourcesAndOutputsTab } from "../../features/agents/static/resourcesAndOutputsTab.js";
import { TeammateAgentCallsTab } from "../../features/agents/teammateAgentCallsTab.js";
import { TeammateAgentConfigurationTab } from "../../features/agents/teammateAgentConfigurationTab.js";
import { TeammateAgentNameCrumbLabel } from "../../features/agents/teammateAgentNameCrumbLabel.js";
import { TeammateAgentOverviewTab } from "../../features/agents/teammateAgentOverviewTab.js";
import { TeammateAgentResultAssessment } from "../../features/agents/teammateAgentResultAssessment.js";
import { TeammateAgentResultOverview } from "../../features/agents/teammateAgentResultOverview.js";
import { TeammateAgentResultTranscript } from "../../features/agents/teammateAgentResultTranscript.js";
import { TeammateAgentTestingTab } from "../../features/agents/teammateAgentTestingTab.js";
import { ToolNameCrumbLabel } from "../../features/tools/toolNameCrumbLabel.js";
import { AutomationResultRedirectPage } from "../../pages/automations/automationResultRedirectPage.js";
import { TeammateAgentPage } from "../../pages/teammateAgentPage.js";
import { TeammateAgentsPage } from "../../pages/teammateAgentsPage.js";
import { ToolDetailsPage } from "../../pages/toolDetailsPage.js";
import { ToolsPage } from "../../pages/toolsPage.js";
import { appPaths } from "../appPaths.js";
import { defineHandle } from "../navigation/appRouteHandle.js";
import { AgentsNavigationMenu } from "../navigation/menu/agentsNavigationMenu.js";
import { WorkspaceId } from "../navigation/workspaces.js";
import { requireRouteParam } from "../routeParams.js";
import {
  AgentCallsCompatRedirect,
  GradingTupleRedirect,
} from "./compatRedirects.js";
import {
  createCriterionDrilldownRoute,
  createGradingSlideoverRoute,
} from "./gradingRoutes.js";

/** The top-level routes of the Agents workspace. */
export function createAgentsWorkspaceRoutes(): RouteObject[] {
  return [
    {
      path: appPaths.agentTools.pattern,
      handle: defineHandle({
        workspaceId: WorkspaceId.Agents,
        navigationMenu: <AgentsNavigationMenu />,
        crumbs: ({ pathname }) => [{ label: "Tools", href: pathname }],
      }),
      children: [
        {
          index: true,
          Component: ToolsPage,
        },
        {
          path: ":toolId",
          Component: ToolDetailsPage,
          handle: defineHandle({
            crumbs: ({ params }) => [
              { label: <ToolNameCrumbLabel toolId={params.toolId} /> },
            ],
            pageContext: ({ params }) => ({
              page: "toolConfiguration",
              toolId: params.toolId,
            }),
          }),
        },
      ],
    },
    {
      // Playbook-free report link from notification emails; resolves the
      // result's automation + playbook and redirects to the canonical page.
      path: appPaths.automationResult.pattern,
      Component: AutomationResultRedirectPage,
    },
    {
      path: appPaths.legacyAutomationResult.pattern,
      Component: AutomationResultRedirectPage,
    },
    {
      path: appPaths.teammateAgents.pattern,
      handle: defineHandle({
        workspaceId: WorkspaceId.Agents,
        navigationMenu: <AgentsNavigationMenu />,
        crumbs: ({ pathname }) => [{ label: "Agents", href: pathname }],
      }),
      children: [
        {
          index: true,
          Component: TeammateAgentsPage,
        },
        {
          path: ":teammateAgentId",
          Component: TeammateAgentPage,
          handle: defineHandle({
            crumbs: ({ params }) => [
              {
                label: (
                  <TeammateAgentNameCrumbLabel
                    teammateAgentId={params.teammateAgentId}
                  />
                ),
              },
            ],
            pageContext: ({ params }) => ({
              page: "teammateAgent",
              teammateAgentId: params.teammateAgentId,
            }),
          }),
          children: [
            { index: true, element: <Navigate to="overview" replace /> },
            {
              path: "overview",
              Component: TeammateAgentOverviewTab,
              children: [
                // Note: teammateId param is redundant with teammateAgentId, but required because
                // TrainingTeammatePagePlaybookCriterion uses useParams() to look for 'teammateId'.
                // We include both in the route so the component can find teammateId via useParams().
                createCriterionDrilldownRoute({
                  // An agent's pages have never excluded calls without
                  // grades, so this drilldown must not either.
                  appliesHasResultDefault: false,
                  path: "teammates/:teammateId/playbooks/:playbookId/criteria/:criterionId",
                  buildCriterionPath: (params, criterionId) =>
                    appPaths.teammateAgentCriterionCall.build({
                      teammateAgentId: requireRouteParam(
                        params,
                        "teammateAgentId",
                      ),
                      teammateId: requireRouteParam(params, "teammateId"),
                      playbookId: requireRouteParam(params, "playbookId"),
                      criterionId,
                      gradingId: requireRouteParam(params, "gradingId"),
                    }),
                  buildTupleRedirectTo: (params, gradingId) =>
                    appPaths.teammateAgentCriterionCall.build({
                      teammateAgentId: requireRouteParam(
                        params,
                        "teammateAgentId",
                      ),
                      teammateId: requireRouteParam(params, "teammateId"),
                      playbookId: requireRouteParam(params, "playbookId"),
                      criterionId: requireRouteParam(params, "criterionId"),
                      gradingId,
                    }),
                }),
              ],
            },
            {
              path: "configuration",
              Component: TeammateAgentConfigurationTab,
            },
            // Static agents author their prompt as five sections across these two
            // tabs; the page renders them inside the static agent layout.
            {
              path: "instructions",
              Component: InstructionsTab,
            },
            {
              path: "resources-outputs",
              Component: ResourcesAndOutputsTab,
            },
            {
              path: "testing",
              Component: TeammateAgentTestingTab,
              children: [
                {
                  path: "specs/:testSpecId/runs/:testRunId/results/:testResultId",
                  Component: TeammateAgentResultOverview,
                  children: [
                    {
                      index: true,
                      element: <Navigate to="assessment" replace />,
                    },
                    {
                      path: "assessment",
                      Component: TeammateAgentResultAssessment,
                    },
                    {
                      path: "transcript",
                      Component: TeammateAgentResultTranscript,
                    },
                  ],
                },
              ],
            },
            {
              path: "customer-interactions",
              Component: TeammateAgentCallsTab,
              children: [
                // The grading detail is keyed purely by gradingId; teammate/account
                // are resolved from the grading, so the redundant teammate/playbook
                // segments the slideover used to carry are gone.
                createGradingSlideoverRoute({
                  path: ":gradingId",
                  buildCriterionPath: (params, criterionId) =>
                    appPaths.teammateAgentCallCriterion.build({
                      teammateAgentId: requireRouteParam(
                        params,
                        "teammateAgentId",
                      ),
                      gradingId: requireRouteParam(params, "gradingId"),
                      criterionId,
                    }),
                }),
                // Backwards-compatibility redirect for old tuple URLs.
                {
                  path: "teammates/:teammateId/playbooks/:playbookId/accounts/:accountId/*",
                  element: (
                    <GradingTupleRedirect
                      buildTo={(params, gradingId) =>
                        appPaths.teammateAgentCall.build({
                          teammateAgentId: requireRouteParam(
                            params,
                            "teammateAgentId",
                          ),
                          gradingId,
                        })
                      }
                    />
                  ),
                },
              ],
            },
            // Backwards-compatibility catch-all for the old `calls` tab segment
            // (the bare `/calls` list and old tuple detail URLs); these now live
            // under `customer-interactions`.
            {
              path: "calls/*",
              element: <AgentCallsCompatRedirect />,
            },
          ],
        },
      ],
    },
  ];
}
